/*
  A tiny IndexedDB key-value wrapper for browser-only usage.
  Provides get/set/delete/clear operations on a single object store.
*/

const DB_NAME = 'AssistivAppStorage';
const DB_VERSION = 1;
const STORE_NAME = 'kv';

// Guard for non-browser environments
const isBrowser = typeof window !== 'undefined' && typeof indexedDB !== 'undefined';

function openDB(): Promise<IDBDatabase> {
  if (!isBrowser)
    return Promise.reject(new Error('IndexedDB is not available in this environment'));

  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'key' });
      }
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
    request.onblocked = () => {
      // If there's an open connection elsewhere, we just log; the caller can retry
      console.warn('IndexedDB open blocked. Close other tabs or reload to continue.');
    };
  });
}

const IndexedDBService = {
  available(): boolean {
    return isBrowser;
  },

  async get<T = unknown>(key: string): Promise<T | undefined> {
    if (!isBrowser) return undefined;
    const db = await openDB();

    return new Promise<T | undefined>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readonly');
      const store = tx.objectStore(STORE_NAME);
      const req = store.get(key);

      req.onsuccess = () => {
        const rec = req.result as { key: string; value: T } | undefined;
        resolve(rec?.value);
      };
      req.onerror = () => reject(req.error);
    });
  },

  async set<T = unknown>(key: string, value: T): Promise<void> {
    if (!isBrowser) return;
    const db = await openDB();

    return new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      const req = store.put({ key, value });

      req.onsuccess = () => resolve();
      req.onerror = () => reject(req.error);
    });
  },

  async delete(key: string): Promise<void> {
    if (!isBrowser) return;
    const db = await openDB();

    return new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      const req = store.delete(key);

      req.onsuccess = () => resolve();
      req.onerror = () => reject(req.error);
    });
  },

  async clear(): Promise<void> {
    if (!isBrowser) return;
    const db = await openDB();

    return new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      const req = store.clear();

      req.onsuccess = () => resolve();
      req.onerror = () => reject(req.error);
    });
  },
};

export default IndexedDBService;
